import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Select,
  MenuItem,
  FormControl,
  Paper,
  Grid,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  CircularProgress,
} from '@mui/material';
import ReusableComparisonTable from './Components/ReusableComparisonTable';
import { KeyboardArrowDown } from '@mui/icons-material';
import { TDSDetailAction } from './APIs/action';
import { toast } from 'react-toastify';
import Entity from '../../../v3/Common/Entity';
import { useSelector } from 'react-redux';
import TDSTable from './Components/TDSTable';
import StickyEntity from '../../../v3/Common/Entity/StickyEntity';
import { MODULE_PERMISSION_ENUM } from '../../../v3/Sidebar/Enum';
import NoAccess from '../../../v3/Common/NoAccess/NoAccess';

const CumulativeTDS = () => {
  const [TDSData, setTDSData] = useState([]);
  const [loader, setLoader] = useState(false);
  const [selectedYear, setSelectedYear] = useState(
    TDSData[0]?.financial_year || '',
  );

  // Get manufacturer and entity from Redux via Entity component
  const {
    selectedSupplierData,
    selectedSupplier: selectedManufacturer,
    selectedEntity: selectedCompanyEntity,
    selectedEntityInfo,
  } = useSelector(
    (state) => state.entityReducer
  );
  const hasAccess = selectedEntityInfo?.has_access;
  useEffect(() => {
    if (selectedCompanyEntity && hasAccess !== false) {
      getTDSDetail(selectedCompanyEntity);
    } else {
      setTDSData([]);
    }
  }, [selectedCompanyEntity, hasAccess]);
  
  const selectedData = TDSData?.find((d) => d?.financial_year === selectedYear);
  
  const getTDSDetail = async (company_esi_id) => {
    setLoader(true);
    const { data, message, error } = await TDSDetailAction(company_esi_id);
    setLoader(false);
    if (!error) {
      setTDSData(data);
      setSelectedYear(data[0]?.financial_year || '');
    } else {
      toast.error(message || 'Failed to fetch TDS details');
    }
  };
  const isEntityEnabled = selectedSupplierData?.entity_enabled;
  return (
    <>
      <StickyEntity module_name={MODULE_PERMISSION_ENUM.CUMULATIVE_TDS} />
      {hasAccess === false ?(
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '60vh',
          }}
        >
          <NoAccess />
        </Box>
      ) : (
      <Box sx={{ mt: {xs : isEntityEnabled ? '96px': 0, md: 0 },p: 2, display: 'flex', flexDirection: 'column', gap: { xs:1, md:2 } }}>
        {TDSData && TDSData.length > 0 && (
          <>
            <Typography
              variant='labelS'
              sx={{ color: '#666', mb: 0.5 }}
            >
              Financial Year
            </Typography>
            <FormControl
              fullWidth
              sx={{ mb: 3 }}
            >
              <Select
                value={selectedYear}
                size='small'
                onChange={(e) => setSelectedYear(e.target.value)}
              >
                {TDSData.map((fy) => (
                  <MenuItem
                    key={fy?.financial_year}
                    value={fy?.financial_year}
                  >
                    {fy?.financial_year}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </>
        )}
        {selectedData && (
          <Grid
            container
            spacing={2}
          >
            <Grid
              item
              xs={12}
              sm={6}
              sx={{
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              <Accordion
                elevation={0}
                defaultExpanded={true}
                sx={{
                  borderRadius: '16px',
                  backgroundColor: '#FFFFFF',
                  '&:before': {
                    display: 'none',
                  },
                }}
              >
                <AccordionSummary
                  expandIcon={<KeyboardArrowDown />}
                  aria-controls='tds-content'
                  id='tds-header'
                  sx={{ 
                    px: 2,
                    cursor: 'default !important',
                  }}
                >
                  <Box>
                    <Typography variant='overline'>
                      {selectedData?.tds_summary?.title}
                    </Typography>
                    <Typography
                      variant='h2'
                      sx={{ color: '#CC0000', mt: 1 }}
                    >
                      {selectedData?.tds_summary?.total_amount || '₹ 0'}
                    </Typography>
                  </Box>
                </AccordionSummary>
                <AccordionDetails sx={{ px: 2, pb: 2 }}>
                  {selectedData?.tds_summary?.breakdown ? (
                    <TDSTable breakdown={selectedData?.tds_summary?.breakdown} />
                  ) : (
                    <Typography>No breakdown data available</Typography>
                  )}
                </AccordionDetails>
              </Accordion>
            </Grid>

            <Grid
              item
              xs={12}
              sm={6}
              sx={{ display: 'flex', flexDirection: 'column' }}
            >
              <Paper
                elevation={0}
                sx={{
                  boxShadow: 'none',
                  backgroundColor: '#FFFFFF',
                  p: 2,
                }}
              >
                {selectedData?.summary_cards &&
                  selectedData?.summary_cards.map((card, index) => (
                    <Box
                      key={index}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        py: '8px',
                        mb: '4px',
                        borderTop:
                          card?.type === 'divider' ? '1px solid #D9D9D9' : 'none',
                      }}
                    >
                      <Typography
                        variant='labelD'
                        color='textSecondary'
                        sx={{
                          color: card?.type === 'divider' ? '#333333' : '#636363',
                        }}
                      >
                        {card?.label || ''}
                      </Typography>
                      <Typography
                        variant='body1'
                        sx={{
                          fontWeight: 600,
                          color:
                            card?.type === 'fail'
                              ? '#CC0000'
                              : card?.type === 'success'
                              ? '#008000'
                              : card?.type === 'divider'
                              ? '#333333'
                              : '#666666',
                        }}
                      >
                        {card?.value || ''}
                      </Typography>
                    </Box>
                  ))}
              </Paper>
            </Grid>
          </Grid>
        )}
        
      </Box>
      )}
    </>
  );
};

export default CumulativeTDS;
